"use client";

import * as React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  Grid,
  GridItem,
  Text,
  Spinner,
  Flex,
} from "@chakra-ui/react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

import {
  usePhotoEvidence,
  useGeolocationCapture,
  useOtpVerification,
  VET_OFFICERS,
} from "../utils/field-work-utils";
import { getCounties, getCurrentTime } from "../utils/getCounties";
import { FaMapMarkerAlt } from "react-icons/fa";
import { PhotoEvidenceField } from "./PhotoEvidenceField";
import { useHistory } from "react-router-dom";
import { fieldLogSchema } from "../utils/fieldLogSchema";
import { useSubmitFieldWorkLog } from "../hooks/useSubmitFieldWork";
import { useSuppliers } from "../hooks/useSuppliers";

function showAlert({ title, message, isError = false }) {
  confirmAlert({
    title,
    message,
    closeOnEscape: true,
    closeOnClickOutside: true,
    buttons: [
      {
        label: "OK",
        style: isError
          ? { background: "#e53e3e", color: "#fff" }
          : { background: "#494395", color: "#fff" },
      },
    ],
  });
}
export default function FieldLogPage() {
  const router = useHistory();
  const { data: supplier } = useSuppliers();

  const [loading, setLoading] = useState(false);
  const [submittingPayload, setSubmittingPayload] = useState(null);

  const { preview, setFile, clearFile } = usePhotoEvidence((file) =>
    setValue("photo_evidence_url", file, { shouldValidate: true }),
  );

  const { capturing: capturingGPS, capture: captureGPSLocation } =
    useGeolocationCapture((coords) =>
      setValue("gps_coordinates", coords, {
        shouldValidate: true,
        shouldDirty: true,
      }),
    );

  const otp = useOtpVerification();

  const countyOptions = getCounties();

  const submitLog = useSubmitFieldWorkLog();

  const { register, handleSubmit, watch, setValue, getValues, formState } =
    useForm({
      resolver: yupResolver(fieldLogSchema),
      defaultValues: {
        visit_date: new Date().toISOString().split("T")[0],
        visit_start_time: getCurrentTime(),
        visit_end_time: "",
        staffName: "",
        county: "",
        location: "",
        contact_phonenumber: "",
        activityDescription: "",
        photo_evidence_url: null,
        gps_coordinates: "",
      },
    });
  const watchedSupplierId = watch("supplier_id");
  const photoFieldValue = watch("photo_evidence_url");
  const selectedSupplier = supplier?.find(
    (supp) => supp.id === watchedSupplierId,
  );
  const errors = formState.errors;
  // Monitor both the supplier selection and whether the user modified the phone field
  const isPhoneFieldEdited = formState.touchedFields.contact_phonenumber;

  useEffect(() => {
    if (!selectedSupplier) return;

    // ONLY autofill if the user hasn't manually edited the input field yet
    if (!isPhoneFieldEdited) {
      setValue(
        "contact_phonenumber",
        selectedSupplier.contact_phonenumber ?? "",
        {
          shouldValidate: true, // Trigger validation so the red error goes away
          shouldDirty: false, // Keep the field marked as pristine/original
        },
      );
    }
  }, [watchedSupplierId, selectedSupplier, setValue, isPhoneFieldEdited]);

  const photoFieldError = errors.photo_evidence_url;

  const captureEndTime = () => {
    setValue("visit_end_time", getCurrentTime(), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onSubmit = (data) => {
    setSubmittingPayload(data);
    confirmAlert({
      title: "Confirm Submission",
      message: `You are about to submit the field work visit log for ${data.staffName}. Do you wish to continue?`,
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: "Yes, Submit",
          style: { background: "#494395", color: "#fff" },
          onClick: () => handleFinalVerifiedSubmission(data),
        },
        {
          label: "Cancel",
          style: { background: "#e2e8f0", color: "#1a202c" },
        },
      ],
    });
  };

  const onInvalid = (validationErrors) => {
    showAlert({
      title: "Form Validation Failed",
      message: `Please fix the ${Object.keys(validationErrors).length} missing or invalid field(s) highlighted below.`,
      isError: true,
    });
  };

  const handleFinalVerifiedSubmission = async (data) => {
    const payload = data ?? submittingPayload;
    if (!payload) return;

    if (!otp.code.trim()) {
      showAlert({
        title: "Validation Error",
        message: "Please enter the 5-digit OTP code received by the supplier.",
        isError: true,
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("action", "submit_log");
      formData.append("contact_phonenumber", payload.contact_phonenumber);
      formData.append("visit_date", payload.visit_date);
      formData.append("field_staff_name", payload.staffName);
      formData.append("county", payload.county);
      formData.append("location", payload.location);
      formData.append("supplier_id", payload.supplier_id);
      formData.append("activity_description", payload.activityDescription);
      formData.append("entered_otp_code", otp.code);
      formData.append("visit_start_time", payload.visit_start_time);
      formData.append("visit_end_time", payload.visit_end_time);
      formData.append("gps_coordinates", payload.gps_coordinates);

      if (payload.photo_evidence_url instanceof File) {
        formData.append("photo_evidence_url", payload.photo_evidence_url);
      }

      const result = await submitLog(formData);

      if (result.error) {
        showAlert({
          title: "Submission Failed",
          message: result.error,
          isError: true,
        });
        return;
      }

      showAlert({
        title: "Log Saved",
        message: "The Field Work Visit Log has been captured successfully.",
      });
    } catch (err) {
      console.error("Submission error:", err);
      showAlert({
        title: "Network Failure",
        message: "An unexpected error occurred. Please try again.",
        isError: true,
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box minH="100vh" bg="gray.50" display="flex" flexDirection="column">
      <Box
        as="main"
        flex={1}
        maxW="4xl"
        w="full"
        mx="auto"
        p={{ base: 4, sm: 6, lg: 8 }}
      >
        <Box
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="2xl"
          p={{ base: 4, sm: 6 }}
          boxShadow="xs"
        >
          {/* Form Header */}
          <Flex
            gap={3}
            borderBottom="1px solid"
            borderColor="gray.100"
            pb={4}
            mb={6}
          >
            <Box>
              <Text as="h1" fontSize="xl" fontWeight="black" color="gray.900">
                Field Work Visit Log
              </Text>
            </Box>
          </Flex>

          <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
            <VStack spacing={6} align="stretch">
              {/* ── 1. Visit Details ── */}
              <VStack spacing={4} align="stretch">
                <Grid
                  templateColumns={{ base: "1fr", sm: "repeat(3, 1fr)" }}
                  gap={4}
                >
                  {/* Visit Date */}
                  <GridItem>
                    <FormControl isInvalid={!!errors.visit_date}>
                      <FormLabel>Date *</FormLabel>
                      <Input
                        type="date"
                        bg="gray.50"
                        borderColor="gray.200"
                        h={10}
                        {...register("visit_date")}
                      />
                      <FormErrorMessage>
                        {errors.visit_date?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>

                  {/* Visit Start Time */}
                  <GridItem>
                    <FormControl isInvalid={!!errors.visit_start_time}>
                      <FormLabel>Visit Start Time *</FormLabel>
                      <Input
                        type="time"
                        h={10}
                        {...register("visit_start_time")}
                      />
                      <FormErrorMessage>
                        {errors.visit_start_time?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>

                  {/* Field Staff Name */}
                  <GridItem>
                    <FormControl isInvalid={!!errors.staffName}>
                      <FormLabel>Vet Officer Name *</FormLabel>
                      <Select
                        bg="gray.50"
                        borderColor="gray.200"
                        h={10}
                        placeholder="Select Vet Officer name"
                        {...register("staffName")}
                      >
                        {VET_OFFICERS.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage>
                        {errors.staffName?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                </Grid>

                <Grid
                  templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                  gap={4}
                >
                  {/* County */}
                  <GridItem>
                    <FormControl isInvalid={!!errors.county}>
                      <FormLabel>County *</FormLabel>
                      <Select
                        bg="gray.50"
                        borderColor="gray.200"
                        h={10}
                        placeholder="Select County"
                        {...register("county")}
                        onChange={(e) => {
                          register("county").onChange(e);
                          setValue("location", "", { shouldValidate: false });
                        }}
                      >
                        {countyOptions.map((county) => (
                          <option key={county.value} value={county.value}>
                            {county.label}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage>
                        {errors.county?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>

                  {/* Location */}
                  <GridItem>
                    <FormControl isInvalid={!!errors.location}>
                      <FormLabel>Location *</FormLabel>
                      <Input
                        bg="gray.50"
                        borderColor="gray.200"
                        h={10}
                        placeholder="e.g. Kiambu Town"
                        {...register("location")}
                      />
                      <FormErrorMessage>
                        {errors.location?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                </Grid>
              </VStack>

              {/* ── 2. Contact on Site ── */}
              <VStack
                spacing={4}
                marginTop={30}
                align="stretch"
                width={{ base: "95%", md: "80%" }}
              >
                {/* SUPPLIER NAME SELECT DROPDOWN */}
                <FormControl isInvalid={errors?.supplier_id?.message}>
                  <FormLabel>Supplier's Name</FormLabel>
                  <Select
                    placeholder="Select agent/supplier..."
                    focusBorderColor="brand.800"
                    {...register("supplier_id", {
                      required: "Supplier is required",
                    })}
                  >
                    {supplier
                      ?.sort((a, b) => {
                        // Fallback to empty string if a.name or b.name is null/undefined
                        const nameA = a?.name ?? "";
                        const nameB = b?.name ?? "";
                        return nameA.localeCompare(nameB);
                      })
                      .map((supplier) => (
                        <option key={supplier.id} value={supplier?.id}>
                          {supplier?.name}
                        </option>
                      ))}
                  </Select>
                  <FormErrorMessage>
                    {errors?.supplier_id?.message}
                  </FormErrorMessage>
                </FormControl>

                {/* AUTOPOPULATED BUT EDITABLE PHONE NUMBER FIELD */}
                <FormControl isInvalid={errors?.contact_phonenumber?.message}>
                  <FormLabel>Supplier's Phone Number</FormLabel>
                  <Input
                    type="tel"
                    placeholder="Enter supplier phone number..."
                    focusBorderColor="brand.800"
                    {...register("contact_phonenumber", {
                      required: "Phone number is required",
                    })}
                  />
                  <FormErrorMessage>
                    {errors?.contact_phonenumber?.message}
                  </FormErrorMessage>
                </FormControl>
              </VStack>

              {/* ── 3. Activity ── */}
              <VStack
                spacing={4}
                align="stretch"
                pt={4}
                borderTop="1px solid"
                borderColor="gray.100"
              >
                {/* ✅ Activity Description — was missing from JSX */}
                <FormControl isInvalid={!!errors.activityDescription}>
                  <FormLabel>Activity Description *</FormLabel>
                  <Textarea
                    placeholder="Describe what was done during this visit..."
                    bg="gray.50"
                    borderColor="gray.200"
                    fontSize="sm"
                    minH="100px"
                    {...register("activityDescription")}
                  />
                  <FormErrorMessage>
                    {errors.activityDescription?.message}
                  </FormErrorMessage>
                </FormControl>
                <PhotoEvidenceField
                  value={
                    photoFieldValue instanceof File ? photoFieldValue : null
                  }
                  preview={preview}
                  onSelect={setFile}
                  onClear={clearFile}
                  fieldName="photo_evidence_url"
                  error={photoFieldError}
                />
              </VStack>

              {/* ── GPS Verification ── */}
              <VStack spacing={3} align="stretch">
                <Flex
                  justifyContent="space-between"
                  alignItems="flex-start"
                  gap={3}
                  flexWrap="wrap"
                >
                  <VStack align="start" spacing={1}>
                    <Text>GPS Verification Coordinates *</Text>
                    <Text fontSize="11px" color="gray.500">
                      Capture visit location for verification.
                    </Text>
                  </VStack>
                  <Button
                    type="button"
                    onClick={captureGPSLocation}
                    isDisabled={capturingGPS}
                    bg="#2AB4AA"
                    _hover={{ bg: "#22998f" }}
                    color="white"
                    fontWeight="bold"
                    leftIcon={<FaMapMarkerAlt size={16} />}
                  >
                    {capturingGPS ? "Capturing..." : "Capture GPS"}
                  </Button>
                </Flex>
                <FormControl isInvalid={!!errors.gps_coordinates}>
                  <Input
                    isReadOnly
                    bg="gray.100"
                    borderColor="gray.200"
                    h={10}
                    fontWeight="medium"
                    placeholder="GPS coordinates will appear here..."
                    {...register("gps_coordinates")}
                  />
                  <FormErrorMessage>
                    {errors.gps_coordinates?.message}
                  </FormErrorMessage>
                </FormControl>
              </VStack>

              {/* ── Visit End Time ── */}
              <VStack spacing={3} align="stretch">
                <Flex
                  justifyContent="space-between"
                  alignItems="flex-start"
                  gap={3}
                  flexWrap="wrap"
                >
                  <VStack align="start" spacing={1}>
                    <Text>Visit Completion Time *</Text>
                    <Text fontSize="11px" color="gray.500">
                      Capture official visit completion timestamp.
                    </Text>
                  </VStack>
                  <Button
                    type="button"
                    onClick={captureEndTime}
                    isDisabled={otp.sending}
                    bg="#2AB4AA"
                    _hover={{ bg: "#22998f" }}
                    color="white"
                  >
                    Capture End Time
                  </Button>
                </Flex>
                <FormControl isInvalid={!!errors.visit_end_time}>
                  <Input
                    type="time"
                    isReadOnly
                    bg="gray.100"
                    borderColor="gray.200"
                    h={10}
                    fontWeight="semibold"
                    {...register("visit_end_time")}
                  />
                  <FormErrorMessage>
                    {errors.visit_end_time?.message}
                  </FormErrorMessage>
                </FormControl>
              </VStack>

              {/* ── OTP Verification ── */}
              <Box
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                p={4}
                bg="gray.50"
              >
                <VStack spacing={3} align="stretch">
                  <Flex
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={3}
                    flexWrap="wrap"
                  >
                    <VStack align="start" spacing={1}>
                      <Text
                        fontSize="xs"
                        fontWeight="bold"
                        textTransform="uppercase"
                        letterSpacing="wider"
                        color="gray.700"
                      >
                        Contact OTP Verification
                      </Text>
                      <Text fontSize="11px" color="gray.500">
                        Send verification code to the on-site contact's mobile
                        number.
                      </Text>
                    </VStack>
                    <Button
                      type="button"
                      onClick={() => otp.send(getValues("contact_phonenumber"))}
                      isDisabled={otp.sending}
                      bg="#2AB4AA"
                      _hover={{ bg: "#22998f" }}
                      color="white"
                    >
                      {otp.sending ? "Sending..." : "Send OTP"}
                    </Button>
                  </Flex>

                  {otp.sent && (
                    <Input
                      type="text"
                      maxLength={5}
                      placeholder="Enter 5-digit OTP"
                      value={otp.code}
                      onChange={(e) => otp.setCode(e.target.value)}
                      bg="white"
                      borderColor="gray.200"
                      textAlign="center"
                      fontWeight="bold"
                      letterSpacing="widest"
                    />
                  )}
                </VStack>
              </Box>

              {/* ── Submit ── */}
              <Button
                type="submit"
                isDisabled={loading}
                w="100%"
                size="lg"
                bg="#494395"
                _hover={{ bg: "#3b3678" }}
                color="white"
                fontWeight="bold"
                borderRadius="xl"
                mt={4}
              >
                {loading ? (
                  <HStack spacing={2}>
                    <Spinner size="sm" />
                    <Text>Submitting...</Text>
                  </HStack>
                ) : (
                  "Submit Field Work Log"
                )}
              </Button>
            </VStack>
          </form>
        </Box>
      </Box>
    </Box>
  );
}
